import { FC, useContext, useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import TagManager from "react-gtm-module";
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from "react-router-dom";

import { ErrorBoundary, ScrollToTop, SMSNotificationsOptIn } from "components";
import NotFound from "components/NotFound";
import {
  AboutDealOptions,
  AddRoyaltyAccount,
  AssetDetail,
  BuyerDashboard,
  CompleteAccountEntry,
  CreateAccountEntry,
  CreateAccountReferralEntry,
  DealPage,
  InstantOfferDetail,
  Layout,
  MyListings,
  OpenListings,
  Overview,
  PastDeals,
  PaymentPortal,
  Profile,
  SellerDashboard,
  SellerLogin,
  ValuationLoading,
  ValuationResults,
  VerifyInvestor,
} from "containers";
import { AppContext } from "contexts";
import { getQueryParams, trackViewEvents } from "lib/helpers";
import * as routes from "routes";
import { getPageURL, getRouteInfoFromRouteName } from "routes/config";

import ProtectedRoute from "./ProtectedRoute";

const RedirectToDjango: FC<RouteComponentProps> = ({ location }) => {
  const { domainNames } = useContext(AppContext);
  window.location.replace(
    getPageURL(location.pathname, domainNames) + location.search,
  );
  return null;
};

const PreviewRoutes = () => (
  <Switch>
    <Route
      path={routes.previewListingDeprecated()}
      render={routeProps => {
        const listingId = routeProps.match.params.listingId;
        return <Redirect to={routes.orderBookAssetDetail(listingId)} />;
      }}
    />
    <Route path={routes.previewValuation()} component={ValuationResults} />
    <Route
      path={routes.previewValuationLoading()}
      component={ValuationLoading}
    />
    <Route path={routes.previewOrderFill()} component={InstantOfferDetail} />
    <Redirect to={routes.sellerNotFound()} />
  </Switch>
);

const DirectoryRoutes = () => (
  <Switch>
    <ProtectedRoute
      protectBy="loginRequired"
      path={routes.directoryBuyerDashboard()}
      component={() => <Redirect to={routes.orderBookBuyerDashboard()} />}
    />
    <ProtectedRoute
      protectBy="loginRequired"
      path={routes.smsNotificationsOptIn()}
      component={SMSNotificationsOptIn}
    />
    <ProtectedRoute
      protectBy="loginRequired"
      path={routes.directoryProfile()}
      component={Profile}
    />
    <Route component={RedirectToDjango} />
  </Switch>
);

const OrderBookRoutes = () => (
  <Switch>
    {/* Exchange */}
    <ProtectedRoute
      protectBy="loginRequired"
      path={routes.orderBookMyListingsNoParams()}
      component={MyListings}
    />
    <ProtectedRoute
      protectBy="anonymousOrSetupFinalized"
      path={routes.orderBookOpenListingsNoParams()}
      component={OpenListings}
    />
    <ProtectedRoute
      protectBy="anonymousOrSetupFinalized"
      path={routes.orderBookPastDealsNoParams()}
      component={PastDeals}
    />
    <ProtectedRoute
      protectBy="anonymousOrSetupFinalized"
      path={routes.orderBookAssetDetail()}
      component={AssetDetail}
    />
    <ProtectedRoute
      protectBy="anonymousOrSetupFinalized"
      path={routes.orderBookAssetDetailPreview()}
      component={AssetDetail}
    />
    <ProtectedRoute
      protectBy="loginRequired"
      path={routes.orderBookDealPage()}
      component={DealPage}
    />
    {/* Buyer */}
    <ProtectedRoute
      protectBy="loginRequired"
      path={routes.orderBookBuyerDashboard()}
      component={BuyerDashboard}
    />
    <ProtectedRoute
      protectBy="loginRequired"
      path={routes.orderBookOrderFill()}
      component={InstantOfferDetail}
    />
    <ProtectedRoute
      protectBy="loginRequired"
      path={routes.orderBookPay()}
      component={PaymentPortal}
    />
    <ProtectedRoute
      protectBy="loginRequired"
      path={routes.orderBookVerify()}
      component={VerifyInvestor}
    />
    {/* Deprecated route redirects */}
    <ProtectedRoute
      protectBy="anonymousOrSetupFinalized"
      path={`${routes.orderBook()}/overview/`}
      render={routeProps => {
        const searchParams = new URLSearchParams(routeProps.location.search);
        searchParams.delete("tab");
        return <Redirect to={routes.orderBookOpenListingsNoParams()} />;
      }}
    />
    <ProtectedRoute
      protectBy="anonymousOrSetupFinalized"
      path={`${routes.orderBook()}/standing-orders/`}
      component={() => <Redirect to={routes.orderBookOpenListingsNoParams()} />}
    />
    {/* Match failed redirect */}
    <Redirect to={routes.sellerNotFound()} />
  </Switch>
);

const SellerRoutes = () => (
  <Switch>
    <Route path={routes.sellerNotFound()} component={NotFound} />
    {/* Create account/login */}
    <Route path={routes.sellerCreateAccount()} component={CreateAccountEntry} />
    <Route
      path={routes.sellerCreateAccountReferral()}
      component={CreateAccountReferralEntry}
    />
    <Route
      path={routes.sellerCompleteAccount()}
      component={CompleteAccountEntry}
    />
    <Route path={routes.sellerLogin()} component={SellerLogin} />
    {/* Dashboard */}
    <ProtectedRoute
      protectBy="registeredSeller"
      path={routes.sellerDashboard()}
      component={SellerDashboard}
    />
    {/* Connect routes */}
    <ProtectedRoute
      protectBy="registeredSeller"
      path={routes.sellerAboutDealOptions()}
      component={AboutDealOptions}
    />
    <ProtectedRoute
      protectBy="sellerLoginRequired"
      path={routes.sellerAddRoyaltyAccount()}
      component={AddRoyaltyAccount}
    />
    <ProtectedRoute
      protectBy="registeredSeller"
      path={routes.sellerValuationLoading()}
      component={ValuationLoading}
    />
    <ProtectedRoute
      protectBy="registeredSeller"
      path={routes.sellerValuationResults()}
      component={ValuationResults}
    />
    {/* Deprecated route redirects */}
    <Redirect
      from={`${routes.seller()}/add-statement-set`}
      to={routes.sellerAddRoyaltyAccount()}
    />
    <Redirect
      exact
      from={`${routes.seller()}/deal-page/`}
      to={routes.sellerDashboard()}
    />
    <Redirect
      from={`${routes.seller()}/direct-listing-or-instant-offer/:assetId`}
      to={routes.sellerAddRoyaltyAccount()}
    />
    <Redirect
      from={`${routes.seller()}/direct-listing-request`}
      to={routes.sellerAddRoyaltyAccount()}
    />
    <Redirect
      from={`${routes.seller()}/royalty-account/:royaltyAccountId`}
      to={routes.sellerDashboard()}
    />
    <Redirect
      exact
      from={`${routes.seller()}/your-offers/`}
      to={routes.sellerDashboard()}
    />
    {/* Match failed redirect */}
    <Redirect to={routes.sellerNotFound()} />
  </Switch>
);

const Routes: FC<RouteComponentProps> = ({ location }) => {
  const posthog = usePostHog();
  const [currentDomain, setCurrentDomain] = useState(undefined);
  const { domainNames, user } = useContext(AppContext);

  useEffect(() => {
    const nextDomain = getRouteInfoFromRouteName(location.pathname).domain;

    if (nextDomain !== currentDomain) {
      if (currentDomain === undefined) {
        setCurrentDomain(nextDomain);
        return;
      }
      window.location.replace(getPageURL(location.pathname, domainNames));
    }
    return;
  }, [location.pathname, currentDomain, domainNames]);

  useEffect(() => {
    if (trackViewEvents()) {
      const _hsq = ((window as any)._hsq = (window as any)._hsq || []);
      _hsq.push(["setPath", location.pathname]);
      _hsq.push(["trackPageView"]);

      const hsLiveChat = (window as any).HubSpotConversations;
      if (hsLiveChat) {
        hsLiveChat.widget.load();
        hsLiveChat.widget.refresh();
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (trackViewEvents()) {
      if (!!user && user.userEmail) {
        TagManager.dataLayer({ dataLayer: { userId: user.hubspotVid } });
      }
    }
  }, [user, location.pathname]);

  useEffect(() => {
    if (trackViewEvents()) {
      const queryParams = getQueryParams();
      const events = [];

      if (queryParams.ga_login) {
        events.push("successful_login");
      }
      if (queryParams.ga_seller_sql) {
        events.push("sql");
      }
      if (queryParams.ga_seller_mql) {
        events.push("mql");
      }
      if (queryParams.ga_buyer) {
        events.push("account_created-investor");
      }
      events.forEach(event => {
        TagManager.dataLayer({ dataLayer: { event } });
      });
    }
  }, [location.pathname]);

  return (
    <Layout>
      <ErrorBoundary posthog={posthog}>
        <ScrollToTop />
        <Switch>
          <ProtectedRoute
            path={routes.preview()}
            protectBy="staffRequired"
            component={PreviewRoutes}
          />
          <Route path={routes.directory()} component={DirectoryRoutes} />
          <Route path={routes.orderBook()} component={OrderBookRoutes} />
          <Route path={routes.seller()} render={SellerRoutes} />
          <ProtectedRoute
            protectBy="anonymousOrSetupFinalized"
            path={routes.overview()}
            component={Overview}
          />
          <Redirect exact from="/" to={routes.overview()} />
          <Route component={RedirectToDjango} />
        </Switch>
      </ErrorBoundary>
    </Layout>
  );
};

export default withRouter(Routes);
